import type { ReactNode } from 'react'

import type { CardFrame, CardTextPosition, OccasionType } from '@forbiz/contracts'

import c from './CardView.module.css'

/**
 * 디지털 화환 카드 프레임 렌더러 (BIZ-86/115). **web 수신자 카드와 admin 미리보기가 공유**한다.
 * 라우팅·fetch 없이 순수 props로만 그린다 — 미리보기가 실제 카드와 어긋나지 않게 하는 게 목적.
 *
 * 프레임(코드) + 스킨(데이터):
 *  - PAPER: 종이 카드형(한지) — 괘선 프레임 안에 글이 순서대로 떠오른다.
 *  - NIGHT: 풀스크린 무드형(밤의 국화) — 은은한 빛 속 국화와 문장.
 * 색(bg/text/accent)·배경 이미지는 CSS 변수로 주입한다.
 *
 * preview=true: 진입 애니메이션을 끄고(색 입력마다 재시작 방지) 높이를 컨테이너 기준(100%)으로
 * 잡는다 — admin 모달의 폰 프레임 안에 축소해 넣기 위함(실제 페이지는 100vh).
 */

const OCCASION_LABEL: Record<OccasionType, string> = {
  FUNERAL: '謹弔',
  CELEBRATION: '축하의 마음',
  GIFT: '감사의 마음',
}

/** 국화 한 송이(SVG). 근조 카드의 장식 — accent 색을 따른다. */
function Chrysanthemum({ className }: { className: string | undefined }) {
  const petals: ReactNode[] = []
  for (let i = 0; i < 12; i++) {
    petals.push(<ellipse key={`o${i}`} cx="50" cy="30" rx="5" ry="14" transform={`rotate(${i * 30} 50 50)`} />)
  }
  for (let i = 0; i < 8; i++) {
    petals.push(
      <ellipse key={`i${i}`} cx="50" cy="36" rx="4.2" ry="10" opacity="0.75" transform={`rotate(${i * 45 + 22} 50 50)`} />,
    )
  }
  return (
    <svg className={className} viewBox="0 0 100 100" aria-hidden="true">
      <g fill="currentColor">{petals}</g>
      <circle cx="50" cy="50" r="6.5" fill="currentColor" opacity="0.55" />
    </svg>
  )
}

export interface CardViewProps {
  occasionType: OccasionType
  frame: CardFrame
  bgColor: string
  textColor: string
  accentColor: string
  bgImageUrl?: string | null
  headline?: string | null
  message?: string | null
  senderName?: string | null
  /** IMAGE 프레임 텍스트 위치 프리셋(상단/중앙/하단). 기본 BOTTOM. PAPER/NIGHT는 무시. */
  textPosition?: CardTextPosition
}

export function CardView({
  occasionType,
  frame,
  bgColor,
  textColor,
  accentColor,
  bgImageUrl,
  headline,
  message,
  senderName,
  textPosition = 'BOTTOM',
}: CardViewProps) {
  const vars = {
    '--card-bg': bgColor,
    '--card-text': textColor,
    '--card-accent': accentColor,
  } as React.CSSProperties
  const bgImage = bgImageUrl ? { backgroundImage: `url(${bgImageUrl})` } : undefined
  const label = OCCASION_LABEL[occasionType]
  const isFuneral = occasionType === 'FUNERAL'

  if (frame === 'NIGHT') {
    return (
      <div className={c.night} style={{ ...vars, ...bgImage }}>
        <div className={c.nightInner}>
          <div className={c.nightHalo} aria-hidden="true" />
          {isFuneral ? <Chrysanthemum className={c.nightFlower} /> : null}
          <div className={c.nightLabel}>{label}</div>
          {headline ? <h1 className={c.nightHeadline}>{headline}</h1> : null}
          {message ? <p className={c.nightMessage}>{message}</p> : null}
          <div className={c.nightFrom}>
            <small>보내는 이</small>
            <span>{senderName ?? '—'}</span>
            <i className={c.nightLine} aria-hidden="true" />
          </div>
          <footer className={c.nightFoot}>포비즈 — 마음을 전하는 화환</footer>
        </div>
      </div>
    )
  }

  if (frame === 'IMAGE') {
    // 이미지형 — 배경 아트가 디자인을 담고, 코드는 텍스트만 얹는다. 아무 아트에서도 글이
    // 읽히게 하단 스크림(스킨 배경색 그라데이션)을 깔고 그 위에 텍스트를 둔다.
    const posCls = textPosition === 'TOP' ? c.imageTop : textPosition === 'CENTER' ? c.imageCenter : c.imageBottom
    return (
      <div className={`${c.image} ${posCls}`} style={{ ...vars, ...bgImage }}>
        <div className={c.imageScrim} aria-hidden="true" />
        <div className={c.imageInner}>
          <div className={c.imageLabel}>{label}</div>
          {headline ? <h1 className={c.imageHeadline}>{headline}</h1> : null}
          {message ? <p className={c.imageMessage}>{message}</p> : null}
          <div className={c.imageFrom}>
            <small>보내는 이</small>
            <span>{senderName ?? '—'}</span>
          </div>
          <footer className={c.imageFoot}>포비즈 — 마음을 전하는 화환</footer>
        </div>
      </div>
    )
  }

  return (
    <div className={c.paper} style={{ ...vars, ...bgImage }}>
      <div className={c.paperSheet}>
        <div className={c.paperFrame}>
          <div className={c.paperLabel}>{label}</div>
          {headline ? <h1 className={c.paperHeadline}>{headline}</h1> : null}
          {message ? <p className={c.paperMessage}>{message}</p> : null}
          <div className={c.paperFrom}>
            <small>보내는 이</small>
            {senderName ?? '—'}
          </div>
          {isFuneral ? <Chrysanthemum className={c.paperFlower} /> : null}
        </div>
      </div>
      <footer className={c.paperFoot}>포비즈 — 마음을 전하는 화환</footer>
    </div>
  )
}
